import {
  ProcessQuerySchemaInput,
  StatusResponseSchema,
  ErrorResponseSchema,
  TaskCreationResponse,
  TaskStatusResponse,
  TaskStatus,
  FAQResponseSchema,
  MemoryContext
} from './types';
import { API_BASE_URL } from './config';

// Helper function for making API requests
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint.substring(1) : endpoint}`;
  
  // Set default headers - no auth required
  const headers: HeadersInit = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  };
  
  const defaultOptions: RequestInit = {
    headers,
    mode: 'cors',
  };

  try {
    const response = await fetch(url, { ...defaultOptions, ...options });

    if (!response.ok) {
      let errorData: ErrorResponseSchema | undefined;
      try {
        errorData = await response.json();
      } catch (e) {
        // Ignore if response is not JSON
      }
      
      throw new Error(
        `API request failed: ${response.status} ${response.statusText} - ${
          errorData?.error || 'Unknown error'
        }`
      );
    }

    // Handle cases where the response might be empty
    if (response.status === 204 || response.headers.get('content-length') === '0') {
        return {} as T;
    }

    return await response.json();
  } catch (error) {
    console.error(`Error fetching ${url}:`, error);
    throw error;
  }
}

// --- DERA Endpoints ---

// Validate an OpenRouter API key
export const validateApiKey = async (key: string): Promise<boolean> => {
  try {
    const response = await fetchApi<{valid: boolean}>('api/dera/validate-key', {
      method: 'POST',
      body: JSON.stringify({ key }),
    });
    return response.valid;
  } catch (error) {
    console.error('Error validating API key:', error);
    return false;
  }
};

// Process a query asynchronously through the DERA system
export const processQueryAsync = async (
  input: ProcessQuerySchemaInput
): Promise<TaskCreationResponse> => {
  try {
    return await fetchApi<TaskCreationResponse>('api/dera/process-async', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  } catch (error) {
    // Handle API key validation errors specifically
    if (error instanceof Error && error.message.includes('Invalid OpenRouter API key')) {
      throw new Error('The OpenRouter API key you provided is invalid. Please check and try again.');
    }
    // Re-throw other errors
    throw error;
  }
};

// Check the status of an asynchronous conversation processing - without timeout for long-running conversations
export const getConversationStatus = async (
  conversationId: string
): Promise<TaskStatusResponse> => {
  try {
    const url = `${API_BASE_URL}api/dera/conversation-status/${conversationId}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      }
    });
    
    if (!response.ok) {
      let errorData: ErrorResponseSchema | undefined;
      try {
        errorData = await response.json();
      } catch (e) {
        // Ignore if response is not JSON
      }
      
      throw new Error(
        `API request failed: ${response.status} ${response.statusText} - ${
          errorData?.error || 'Unknown error'
        }`
      );
    }
    
    return await response.json();
  } catch (error: any) {
    console.error(`Error checking conversation status:`, error);
    throw error;
  }
};

// Polling function to get status updates for a conversation
export const getConversationStatusUpdates = async (conversationId: string, 
    onUpdate: (status: TaskStatusResponse) => void,
    interval: number = 2000
): Promise<void> => {
  try {
    const status = await getConversationStatus(conversationId);
    // Call the callback with status update
    onUpdate(status);
    
    // Schedule next update if not completed or error
    if (status.status !== TaskStatus.COMPLETED && status.status !== TaskStatus.ERROR) {
      setTimeout(() => getConversationStatusUpdates(conversationId, onUpdate, interval), interval);
    }
  } catch (error) {
    console.error(`Error getting conversation status updates: ${error}`);
    // Continue polling despite errors
    setTimeout(() => getConversationStatusUpdates(conversationId, onUpdate, interval), interval);
  }
};

// Get the status of the DERA system
export const getDeraStatus = async (): Promise<StatusResponseSchema> => {
  return fetchApi<StatusResponseSchema>('api/dera/status', {
    method: 'GET',
  });
};

// Get FAQ entries
export const getFAQs = async (): Promise<FAQResponseSchema> => {
  return fetchApi<FAQResponseSchema>('api/dera/faqs', {
    method: 'GET',
  });
};

// Get a specific session by ID from the server
export const getSessionFromServer = async (
  sessionId: string
): Promise<{
  id: string;
  memory_context: MemoryContext;
  title: string;
  created_at: string;
  updated_at: string;
}> => {
  try {
    return await fetchApi(`api/dera/session/${sessionId}`, {
      method: 'GET',
    });
  } catch (error) {
    console.error(`Error fetching session ${sessionId}:`, error);
    throw error;
  }
};

// --- Helper Functions for Frontend Use ---

// Removed unused initialization function and random ID generator
